import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {User} from '../../models/user.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {CommonUtils} from '../../utils/common';
import {UserService} from '../../providers/user.service';
import {Subscription} from 'rxjs';
import {mobilePhoneNumberValidator, mustMatch, pwdValidator} from '../../utils/form-validators';
import {ModalController, ToastController} from '@ionic/angular';
import {VetClinicService} from '../../providers/vet-clinic.service';
import {VetClinicStartContextData} from '../../models/vet-clinic-start-context-data.model';
import {ClientsService} from '../../providers/clients.service';
import {Client} from '../../models/client.model';
import {tap} from 'rxjs/operators';
import {AppStateService} from '../../providers/app-state.service';
import {VetClinic} from '../../models/vet-clinic.model';
import {Partner} from '../../models/partner.model';
import {ItemFinderComponent} from '../item-finder/item-finder.component';


@Component({
  selector: 'app-app-edit-user',
  templateUrl: './edit-app-user.component.html',
  styleUrls: ['./edit-app-user.component.scss'],
})
export class EditAppUserComponent implements OnInit, OnDestroy {

  @Input() user: User;
  @Input() csrfToken: string;

  public userGroup: FormGroup;
  private vetClinicStartContext: VetClinicStartContextData;
  private vetClinicStartContextSub: Subscription;
  private userSub: Subscription;
  private hasUnsavedChanges = false;


  @Output() userEditedEmitter = new EventEmitter<User>();
  @Output() userEditCanceledEmitter = new EventEmitter<Boolean>();
  @Output() hasUnsavedChangesEmitter = new EventEmitter<boolean>();
  private loggedUser: User;
  private isAdmin: boolean;
  isGeneralAdmin: boolean;
  isGeneralUserAdmin: boolean;
  isBusy: boolean;
  isBusySub: Subscription;
  pwdType = 'password';
  showPwdIcon: string = 'eye-off-outline';
  pwdSub: Subscription;
  clientSelected: Client | Partner ;
  clinicSelected: VetClinic;
  clinics: VetClinic[];
  clinicSub: Subscription;
  vetcodeId: string;
  partnerBannerActions: boolean;
  isArticleModeEnabled: boolean;

  constructor(
    public commonUtils: CommonUtils,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private toastController: ToastController,
    private vetClinicService: VetClinicService,
    private clientService: ClientsService,
    private appStateService: AppStateService,
    private modalController: ModalController
  ) {
  }

  ngOnInit() {
    this.isBusySub = this.appStateService.isBusySubject.subscribe(isBusy => {
      this.isBusy = isBusy;
    });

    this.vetcodeId = this.user?.vetcodeId;

    this.vetClinicStartContextSub = this.vetClinicService.getVetClinicStartContextData().subscribe((vetClinicStartContext) => {
      this.vetClinicStartContext = vetClinicStartContext;
    });

    this.userSub = this.userService.getUser().subscribe(it => {
        this.loggedUser = this.commonUtils.bestTypedCopyEver<User>(new User(), it);
        this.isAdmin = this.loggedUser.roles.split(';').filter(e => e === 'admin').length === 1;
        this.isGeneralAdmin = this.isAdmin && this.loggedUser.vClientId === null && this.loggedUser.vetcodeId === null;
        this.isGeneralUserAdmin = this.loggedUser.vClientId === null && this.loggedUser.vetcodeId === null;
      }
    );

    if (this.isGeneralAdmin || this.isGeneralUserAdmin) {

        this.userGroup = this.formBuilder.group({
          firstName: [this.user.firstName, Validators.required],
          lastName: [this.user.lastName, Validators.required],
          mobilePhone: [this.user.mobilePhone, [Validators.required, mobilePhoneNumberValidator]],
          email: [this.user.email, Validators.email]
        });
    }
  }


  onEdit() {
    if(this.userGroup.valid){
      this.commonUtils.presentYesNoActionSheet('Na pewno chcesz zapisać zmiany?', 'Tak', 'Nie', 'checkmark', 'close', () => {
        this.appStateService.updateIsBusySubject(true);
        const userEdited: User = this.commonUtils.formGroupValuesToObject<User>(new User(), this.userGroup);

        userEdited.uuid = this.user.uuid;

        if (!this.isGeneralAdmin && !this.isGeneralUserAdmin) {
          userEdited.vClientId = this.loggedUser.vClientId;
          userEdited.vetcodeId = this.loggedUser.vetcodeId;
          userEdited.isPartnerType = this.loggedUser.isPartnerType;
        }
        if(this.user.isPartnerType){
          userEdited.isPartnerType = true;
        }
        this.userService.updateAppUser(userEdited)
          .subscribe((user: User) => {
              this.commonUtils.presentInfoToast('Użytkownik został zmieniony').then(res => {
                this.userEditedEmitter.emit(user);
                this.appStateService.updateIsBusySubject(false);
              });
            },
            error => {
              switch(error.message) {
                case 'USER_NOT_FOUND': {
                  this.commonUtils.presentAlertToast('Brak zalogowanego użytkownika');
                  break;
                }
                case 'EMPTY_ROLES': {
                  this.commonUtils.presentAlertToast('Brak ról użytkownika');
                  break;
                }
                case 'INVALID_ROLES': {
                  this.commonUtils.presentAlertToast('Błędne role użytkownika');
                  break;
                }
                case 'USER_UNAUTHORIZED': {
                  this.commonUtils.presentAlertToast('Nie masz uprawnień');
                  break;
                }
                case 'EMAIL_ALREADY_EXIST': {
                  this.commonUtils.presentAlertToast('Użytkownik o podanym adresie email istnieje');
                  break;
                }
                case 'MOBILE_PHONE_ALREADY_EXIST': {
                  this.commonUtils.presentAlertToast('Użytkownik o podanym nr. telefonu istnieje');
                  break;
                }
                case 'BAD_VCLIENT': {
                  this.commonUtils.presentAlertToast('Klient nie isnieje');
                  break;
                }
                default: {
                  this.commonUtils.presentAlertToast('Wystąpił błąd podczas edycji użytkownika');
                  this.appStateService.updateIsBusySubject(false);
                  break;
                }
              }

              this.appStateService.updateIsBusySubject(false);
            });
      }, () => {
      });
    } else{
      this.commonUtils.presentAlertToast('Błedny formularz');
    }

  }

  onCancelEdit() {
    if (this.hasUnsavedChanges) {
      this.commonUtils.presentYesNoActionSheet('Masz niezapisane zmiany. Na pewno chcesz zrezygnować z zapisu?', 'Tak', 'Nie', 'checkmark', 'close', () => {
        this.userEditCanceledEmitter.emit(true);
      }, () => {
      });
    } else {
      this.userEditCanceledEmitter.emit(true);
    }
  }

  isFormFieldInvalid(form: FormGroup, fieldName: string, validationType: string) {
    return this.commonUtils.isFormFieldInvalid(form, fieldName, validationType);
  }

  isFormInvalid(form: FormGroup, validationType: string) {
    return this.commonUtils.isFormInvalid(form, validationType);
  }

  ngOnDestroy(): void {
    console.log('destroyed')
    this.user = null;
    if (this.vetClinicStartContextSub) {this.vetClinicStartContextSub.unsubscribe();}
    if (this.userSub) {this.userSub.unsubscribe();}
    if (this.isBusySub) {this.isBusySub.unsubscribe();}
  }

  markUnsavedChanges() {
    if (!this.hasUnsavedChanges) {
      this.hasUnsavedChanges = true;
      this.hasUnsavedChangesEmitter.emit(true);
    }
  }

  onChangePwdType() {
    if(this.pwdType === 'password'){
      this.pwdType = 'text';
      this.showPwdIcon = 'eye-outline';
    } else {
      this.pwdType = 'password';
      this.showPwdIcon = 'eye-off-outline';
    }
  }

  generatePwd() {
    this.appStateService.updateIsBusySubject(true);
    this.pwdSub = this.userService.generatePwd().subscribe(pwd=>{
      if(pwd){
        this.pwdType = 'text';
        this.showPwdIcon = 'eye-outline';
        this.userGroup.controls.password.markAsTouched();
        this.userGroup.controls.password.setValue(pwd);
        this.userGroup.controls.retypePassword.setValue(pwd);
        this.commonUtils.presentSimpleInfoToast('Twoje hasło zostało wygenerowane', 'bottom',2000);
      } else {
        this.commonUtils.presentAlertToast('Wystąpił błąd');
      }
      this.appStateService.updateIsBusySubject(false);
    }, error => {
      this.commonUtils.presentAlertToast('Wystąpił błąd');
      this.appStateService.updateIsBusySubject(false);
    });

  }
  selectClinic($event) {
    console.log($event);
    this.vetcodeId = $event.detail.value;
    this.userGroup.controls.vetcodeId.markAsTouched();
    this.markUnsavedChanges();
    console.log(this.userGroup);
  }

   onChooseClient(uuid: any, initialLoad: boolean = true) {
     console.log('wybieramy klienta', initialLoad);
    if(!this.user.isPartnerType){
      this.appStateService.updateIsBusySubject(true);
      this.clinicSub = this.vetClinicService.listClinicsForClient(uuid).subscribe(e => {
        if (e) {
          this.clinics = e;
          console.log('Clinics ==', this.clinics);
          if(initialLoad && !this.user.vetcodeId) {
            this.clinicSelected = null;
            this.vetcodeId = 'not-applicable';
            this.userGroup.controls.vetcodeId.setValue(this.vetcodeId);
            this.userGroup.controls.vetcodeId.markAsTouched();
          }else if (!initialLoad && this.clinics.length === 1) {
            this.clinicSelected = this.clinics[0];
            this.vetcodeId = this.clinicSelected.vetcodeId.toString();
            this.userGroup.controls.vetcodeId.setValue(this.vetcodeId);
            this.userGroup.controls.vetcodeId.markAsTouched();
          } else if(initialLoad && this.user?.vetcodeId){
            this.clinicSelected = this.clinics.filter(s=>s.vetcodeId.toString()==this.user.vetcodeId)[0];
            this.vetcodeId = this.clinicSelected.vetcodeId.toString();
            this.userGroup.controls.vetcodeId.setValue(this.vetcodeId);
            console.log('Selected +', this.clinicSelected);
            console.log(this.clinicSelected);
            console.log(this.clinicSelected.clinicName);
            this.userGroup.controls.vetcodeId.markAsTouched();
          }
          this.appStateService.updateIsBusySubject(false);
        } else {
          this.appStateService.updateIsBusySubject(false);
          this.commonUtils.presentAlertToast('Nie znaleziono lecznic dla podanego klienta');
        }
      });
    } else {
      this.partnerBannerActions = (this.clientSelected as Partner).partnerBannerActions;
      this.isArticleModeEnabled = (this.clientSelected as Partner).isArticleModeEnabled;
    }
  }

  async onSearchClients() {
    const modal = await this.modalController.create({
      component: ItemFinderComponent,
      componentProps: {
        title: 'Wyszukaj klienta',
        itemType: 'clients',
        nameToFind: this.clientSelected?.name ? this.clientSelected.name : '',
        partnersIncluded: this.user.isPartnerType
      }
    });

    modal.onWillDismiss().then(data => {
      if (data && data.data) {
        this.clinicSelected = null;
        this.clientSelected = data.data;
        this.userGroup.controls.vClientId.setValue(this.clientSelected.id);
        this.onChooseClient(this.clientSelected.uuid, false);
      }
    });

    return await modal.present();

  }
  canSelectNotApplicable() {
    if(this.isGeneralAdmin || this.isGeneralUserAdmin) {
      return true;
    }
    return this.isAdmin && (this.loggedUser.vetcodeId === null || this.loggedUser.vetcodeId === undefined);

  }

  getTitleForMyUser() {
    return this.isAdmin ? 'Edycja mojego konta - admin' : 'Edycja mojego konta';
  }
}
